/**
 * Trovva — Transactions v2
 * --------------------------------------------------------------
 * Supabase-wired deal pipeline.  Kanban + List toggle (remembered per user),
 * summary tiles (active / this-month / commission forecast / at-risk),
 * a deal triage feed ("needs attention now"), and click-through to the
 * DealDetailDrawer for full editing, milestone advance, notes & docs.
 *
 * Drop-in replacement for the legacy NJ milestone-timeline view.  The
 * `transactions` / `onUpdateTransaction` / `demoMode` props the old
 * parent passed are accepted but ignored; data is loaded from /api/deals.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, LayoutGrid, List as ListIcon, AlertTriangle, Clock, DollarSign,
  TrendingUp, Search, Filter, RefreshCw, Calendar, Home, User as UserIcon,
  ChevronRight, Upload, Download,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import Badge from '../common/Badge';
import { formatDate } from '../../lib/dateFormat';
// AskAI removed — Brain Chat is the single AI entry point
import DealDetailDrawer from './DealDetailDrawer';
import AgentFilterCombo from '../common/AgentFilterCombo';
import CsvDealImport from './CsvDealImport';

// ── Stage grouping for Kanban ─────────────────────────────────
// We map the raw status values onto 6 human columns.  Each column
// knows which underlying statuses it contains so drag-drop can pick a
// sensible default when the user moves a card.

// Residential pipeline (buyer/seller/lease)
// Post-contract pipeline only — pre-contract stages (leads, search, offer) live in CRM.
// A Deal enters Transactions when the offer is accepted and attorney review begins.
const RESIDENTIAL_STAGES = [
  { key: 'attorney',            label: 'Attorney Review',       statuses: ['attorney_review', 'under_contract', 'offer_accepted'], color: '#8B5CF6' },
  { key: 'inspection',          label: 'Inspection',            statuses: ['inspection'],                                          color: '#06B6D4' },
  { key: 'appraisal',           label: 'Appraisal',             statuses: ['appraisal'],                                           color: '#F59E0B' },
  { key: 'mortgage_commitment', label: 'Mortgage Commitment',   statuses: ['mortgage_commitment'],                                 color: '#2563EB' },
  { key: 'clear_to_close',      label: 'Clear to Close',        statuses: ['clear_to_close'],                                      color: '#16A34A' },
];

// Commercial Sale pipeline
const COMMERCIAL_SALE_STAGES = [
  { key: 'loi',            label: 'LOI',                 statuses: ['loi_submitted', 'loi_negotiation', 'loi_accepted', 'offer_submitted', 'offer_accepted'],  color: '#6366F1' },
  { key: 'due_diligence',  label: 'Due Diligence',       statuses: ['due_diligence', 'inspection', 'appraisal'],      color: '#F59E0B' },
  { key: 'purchase_agmt',  label: 'Purchase Agreement',  statuses: ['attorney_review', 'under_contract'],             color: '#8B5CF6' },
  { key: 'estoppels',      label: 'Estoppels / SNDA',    statuses: ['estoppels_snda'],                                color: '#06B6D4' },
  { key: 'underwriting',   label: 'Financing / UW',      statuses: ['mortgage_commitment'],                           color: '#2563EB' },
  { key: 'clear_to_close', label: 'Clear to close',      statuses: ['clear_to_close'],                                color: '#16A34A' },
];

// Commercial Lease pipeline (NEW — distinct from sale)
const COMMERCIAL_LEASE_STAGES = [
  { key: 'loi',            label: 'LOI',              statuses: ['loi_submitted', 'loi_negotiation', 'loi_accepted'],  color: '#6366F1' },
  { key: 'due_diligence',  label: 'Due Diligence',    statuses: ['due_diligence'],                                    color: '#F59E0B' },
  { key: 'lease_draft',    label: 'Lease Draft',      statuses: ['lease_draft', 'lease_review'],                      color: '#8B5CF6' },
  { key: 'lease_executed', label: 'Lease Executed',   statuses: ['lease_executed'],                                   color: '#06B6D4' },
  { key: 'build_out',      label: 'Build-Out / TI',   statuses: ['build_out'],                                        color: '#2563EB' },
  { key: 'occupancy',      label: 'Occupancy',        statuses: ['occupancy', 'clear_to_close'],                      color: '#16A34A' },
];

// Legacy alias
const COMMERCIAL_STAGES = COMMERCIAL_SALE_STAGES;

// Default to residential
const STAGES = RESIDENTIAL_STAGES;

const STATUS_TO_STAGE = {};
RESIDENTIAL_STAGES.forEach(s => s.statuses.forEach(st => { STATUS_TO_STAGE[st] = s.key; }));
COMMERCIAL_SALE_STAGES.forEach(s => s.statuses.forEach(st => { STATUS_TO_STAGE[`commercial_sale:${st}`] = s.key; }));
COMMERCIAL_LEASE_STAGES.forEach(s => s.statuses.forEach(st => { STATUS_TO_STAGE[`commercial_lease:${st}`] = s.key; }));

// Helper: is a deal commercial?
const isCommercialDeal = (deal) => ['commercial_sale', 'commercial_lease'].includes(deal?.deal_type);

// Helper: get the right stage set for a deal type
const getStagesForDeal = (dealType) => {
  if (dealType === 'commercial_lease') return COMMERCIAL_LEASE_STAGES;
  if (dealType === 'commercial_sale') return COMMERCIAL_SALE_STAGES;
  return RESIDENTIAL_STAGES;
};

const STATUS_LABEL = {
  // Residential statuses — Sales (pre-contract)
  new_lead: 'New lead',
  contacted: 'Contacted',
  qualified: 'Qualified',
  active_search: 'Active search',
  offer_submitted: 'Offer submitted',
  offer_accepted: 'Offer accepted',
  attorney_review: 'Attorney review',
  under_contract: 'Under contract',
  inspection: 'Inspection',
  appraisal: 'Appraisal',
  mortgage_commitment: 'Mortgage commitment',
  clear_to_close: 'Clear to close',
  closed: 'Closed',
  fell_through: 'Fell through',
  cancelled: 'Cancelled',
  // Commercial-specific statuses (NEW — Migration 047)
  loi_submitted: 'LOI submitted',
  loi_negotiation: 'LOI negotiation',
  loi_accepted: 'LOI accepted',
  due_diligence: 'Due diligence',
  lease_draft: 'Lease draft',
  lease_review: 'Lease review',
  lease_executed: 'Lease executed',
  build_out: 'Build-out / TI',
  occupancy: 'Occupancy',
  estoppels_snda: 'Tenant estoppels / SNDA',
  environmental_review: 'Environmental (Phase I/II)',
  zoning_review: 'Zoning & survey review',
};

// Commercial due diligence checklist items (shown in deal detail)
const COMMERCIAL_DD_ITEMS = [
  'Environmental Phase I',
  'Environmental Phase II (if flagged)',
  'Zoning verification',
  'ALTA survey',
  'Title search & commitment',
  'Tenant estoppel certificates',
  'SNDA agreements',
  'Rent roll verification',
  'Operating expense audit',
  'Building condition assessment',
  'ADA compliance review',
];

// ── Helpers ───────────────────────────────────────────────────
const fmtMoney = n => (n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(Number(n)));

const daysUntil = iso => {
  if (!iso) return null;
  const d = new Date(iso); const now = new Date();
  return Math.ceil((d - now) / 86400000);
};

const addressOf = (d) => {
  if (!d) return 'New deal';
  if (d.property?.address) return `${d.property.address}${d.property.city ? `, ${d.property.city}` : ''}`;
  if (d.property_address) return d.property_address;
  const client = d.contact ? [d.contact.first_name, d.contact.last_name].filter(Boolean).join(' ') : null;
  return client ? `${client} deal` : 'New deal';
};

const clientOf = d =>
  d.contact ? [d.contact.first_name, d.contact.last_name].filter(Boolean).join(' ') : null;

// A deal is "at risk" if: closing within 7 days AND no checkpoint in 4+ days,
// OR any of the date fields has passed without being marked done.
const riskFor = deal => {
  const reasons = [];
  const dClose = daysUntil(deal.closing_date);
  if (dClose != null && dClose < 0 && deal.status !== 'closed') reasons.push('Closing date passed');
  else if (dClose != null && dClose <= 7 && dClose >= 0) {
    const lastCk = deal.last_checkpoint_at ? new Date(deal.last_checkpoint_at) : null;
    const stale = !lastCk || (Date.now() - lastCk.getTime()) > 4 * 86400000;
    if (stale) reasons.push(`Closing in ${dClose}d, no recent update`);
  }
  if (deal.attorney_review_end && deal.status === 'attorney_review') {
    const du = daysUntil(deal.attorney_review_end);
    if (du != null && du <= 1) reasons.push(du < 0 ? 'Attorney review expired' : 'Attorney review ends soon');
  }
  if (deal.inspection_deadline) {
    const du = daysUntil(deal.inspection_deadline);
    if (du != null && du < 0 && !deal.inspection_date) reasons.push('Inspection deadline missed');
  }
  return reasons;
};

// ── Main component ────────────────────────────────────────────
const TransactionsView = () => {
  const [view, setView] = useState(() => localStorage.getItem('trovva.tx.view') || 'kanban');
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [txTab, setTxTab] = useState('residential'); // 'residential' | 'commercial_sale' | 'commercial_lease'
  const [selectedId, setSelectedId] = useState(null);
  const [showNewDeal, setShowNewDeal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  // Agent scoping
  const [agents, setAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState('mine');
  const [isBroker, setIsBroker] = useState(false);

  useEffect(() => { localStorage.setItem('trovva.tx.view', view); }, [view]);

  // Load agents + detect role
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const r = await fetch('/api/team', { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (r.ok) {
          const data = await r.json();
          setAgents((data.team || data.members || []).map(u => ({ id: u.id, name: u.full_name || u.name || u.email, email: u.email })));
          setIsBroker(true);
        }
      } catch {}
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      let url = '/api/deals?limit=300';
      if (isBroker && agentFilter === 'mine') url += '&agent_id=me';
      else if (isBroker && agentFilter && agentFilter !== 'all') url += `&agent_id=${agentFilter}`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed to load deals');
      setDeals(j.deals || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [isBroker, agentFilter]);
  useEffect(() => { load(); }, [load]);

  // ── Active pipeline stages based on tab ──────────────────
  const activeStages = txTab === 'commercial_lease' ? COMMERCIAL_LEASE_STAGES
    : txTab === 'commercial_sale' ? COMMERCIAL_SALE_STAGES
    : RESIDENTIAL_STAGES;

  // ── Filtering ────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deals.filter(d => {
      // Tab-level filter: separate residential from commercial
      if (txTab === 'residential' && isCommercialDeal(d)) return false;
      if (txTab === 'commercial_sale' && d.deal_type !== 'commercial_sale') return false;
      if (txTab === 'commercial_lease' && d.deal_type !== 'commercial_lease') return false;
      if (filterStage !== 'all' && STATUS_TO_STAGE[d.status] !== filterStage) return false;
      if (!q) return true;
      return (
        (d.property?.address || '').toLowerCase().includes(q) ||
        (d.property_address || '').toLowerCase().includes(q) ||
        (d.contact?.first_name || '').toLowerCase().includes(q) ||
        (d.contact?.last_name || '').toLowerCase().includes(q) ||
        (d.agent_name || '').toLowerCase().includes(q)
      );
    });
  }, [deals, search, filterStage, txTab]);

  // ── Summary stats (scoped to active tab) ─────────────────
  const stats = useMemo(() => {
    const tabDeals = filtered;
    const active = tabDeals.length;
    const thisMonth = new Date(); thisMonth.setDate(1);
    const nextMonth = new Date(thisMonth); nextMonth.setMonth(nextMonth.getMonth() + 1);
    const closingThisMonth = tabDeals.filter(d => {
      if (!d.closing_date) return false;
      const c = new Date(d.closing_date);
      return c >= thisMonth && c < nextMonth;
    });
    const forecast = tabDeals.reduce((sum, d) => sum + (Number(d.agent_payout) || 0), 0);
    const atRisk = tabDeals.filter(d => riskFor(d).length > 0).length;
    return {
      active,
      closingThisMonth: closingThisMonth.length,
      closingThisMonthValue: closingThisMonth.reduce((s, d) => s + (Number(d.agent_payout) || 0), 0),
      forecast,
      atRisk,
    };
  }, [filtered]);

  // ── Triage feed ──────────────────────────────────────────
  const triage = useMemo(() => {
    const items = [];
    for (const d of deals) {
      const reasons = riskFor(d);
      for (const r of reasons) {
        items.push({ deal: d, reason: r, severity: r.includes('expired') || r.includes('passed') || r.includes('missed') ? 'high' : 'med' });
      }
    }
    // Cap 10
    return items.sort((a, b) => (a.severity === 'high' && b.severity !== 'high' ? -1 : a.severity !== 'high' && b.severity === 'high' ? 1 : 0)).slice(0, 10);
  }, [deals]);

  // ── Kanban columns (uses active tab's stages) ────────────
  const columns = useMemo(() => {
    const map = Object.fromEntries(activeStages.map(s => [s.key, []]));
    for (const d of filtered) {
      // Find which column this deal belongs to based on its status
      const stage = activeStages.find(s => s.statuses.includes(d.status));
      if (stage && map[stage.key]) map[stage.key].push(d);
    }
    return map;
  }, [filtered, activeStages]);

  // ── New deal handler ─────────────────────────────────────
  const createDeal = useCallback(async (payload) => {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch('/api/deals', {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const j = await r.json();
    if (!r.ok) throw new Error(j.error || 'Could not create deal');
    setDeals(prev => [j.deal, ...prev]);
    setShowNewDeal(false);
    setSelectedId(j.deal.id);
  }, []);

  // ── Render ───────────────────────────────────────────────
  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 16,
      }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text, margin: 0 }}>
            Transactions
          </h1>
          <div style={{ color: theme.colors.textLight, fontSize: 13, marginTop: 2 }}>
            {stats.active} active · {stats.closingThisMonth} closing this month
          </div>
        </div>
        {/* Deals are created from Leads → Offer Accepted → Convert to Deal */}
      </div>

      {/* ── Tab bar: Residential | Commercial Sale | Commercial Lease ── */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: `2px solid ${theme.colors.border}`,
        marginBottom: 16,
      }}>
        {[
          { id: 'residential',       label: 'Residential' },
          { id: 'commercial_sale',   label: 'Commercial Sale' },
          { id: 'commercial_lease',  label: 'Commercial Lease' },
        ].map(tab => {
          const active = txTab === tab.id;
          return (
            <button key={tab.id} onClick={() => { setTxTab(tab.id); setFilterStage('all'); }} style={{
              padding: '8px 18px', border: 'none', cursor: 'pointer',
              background: 'transparent',
              color: active ? theme.colors.primary : theme.colors.textLight,
              fontSize: 13, fontWeight: active ? 700 : 500,
              borderBottom: active ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
              marginBottom: -2,
              transition: 'color 0.15s, border-color 0.15s',
            }}>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Summary tiles */}
      <div data-tour="deal-stats" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12, marginBottom: 16,
      }}>
        <SummaryTile
          icon={<Home size={18} color={theme.colors.primary} />}
          label="Active deals"
          value={stats.active}
          sub={`${stats.closingThisMonth} closing this month`}
        />
        <SummaryTile
          icon={<Calendar size={18} color="#10B981" />}
          label="Closing this month"
          value={stats.closingThisMonth}
          sub={fmtMoney(stats.closingThisMonthValue) + ' in agent payouts'}
        />
        <SummaryTile
          icon={<TrendingUp size={18} color="#3B82F6" />}
          label="Commission forecast"
          value={fmtMoney(stats.forecast)}
          sub="Across all active deals"
        />
        <SummaryTile
          icon={<AlertTriangle size={18} color="#EF4444" />}
          label="At risk"
          value={stats.atRisk}
          sub={stats.atRisk ? 'Needs your attention' : 'Nothing urgent'}
          tone={stats.atRisk ? 'warning' : undefined}
        />
      </div>

      {/* Triage feed */}
      {triage.length > 0 && (
        <div data-tour="deal-deadlines" style={{
          border: '1px solid #FED7AA', background: '#FFF7ED', borderRadius: 10,
          padding: 12, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={16} color="#EA580C" />
            <span style={{ fontWeight: 600, color: '#9A3412' }}>Deal triage</span>
            <span style={{ color: '#9A3412', fontSize: 13 }}>· {triage.length} item{triage.length === 1 ? '' : 's'}</span>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {triage.map((t, i) => (
              <button
                key={i}
                onClick={() => setSelectedId(t.deal.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 10px', borderRadius: 8,
                  border: '1px solid ' + (t.severity === 'high' ? '#FECACA' : '#FED7AA'),
                  background: '#fff', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <Badge label={t.severity === 'high' ? 'HIGH' : 'MED'}
                    color={t.severity === 'high' ? '#DC2626' : '#EA580C'} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: theme.colors.text, fontSize: 13 }}>
                      {addressOf(t.deal)}
                    </div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight }}>{t.reason}</div>
                  </div>
                </div>
                <ChevronRight size={16} color={theme.colors.textLight} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div data-tour="deal-pipeline" style={{
        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12,
      }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 320 }}>
          <Search size={14} color={theme.colors.textLight}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search address, client, agent"
            style={{
              width: '100%', padding: '8px 10px 8px 30px', borderRadius: 8,
              border: `1px solid ${theme.colors.border}`, fontSize: 13,
            }}
          />
        </div>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          border: `1px solid ${theme.colors.border}`, borderRadius: 8,
          padding: '4px 8px', fontSize: 13,
        }}>
          <Filter size={14} color={theme.colors.textLight} />
          <select
            value={filterStage}
            onChange={e => setFilterStage(e.target.value)}
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13 }}
          >
            <option value="all">All stages</option>
            {activeStages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        {isBroker && agents.length > 0 && (
          <AgentFilterCombo
            agents={agents}
            value={agentFilter}
            onChange={setAgentFilter}
            mineLabel="My Deals"
            allLabel="All Agents"
          />
        )}

        <button onClick={load} title="Refresh"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 10px', borderRadius: 8,
            border: `1px solid ${theme.colors.border}`, background: '#fff',
            fontSize: 13, cursor: 'pointer',
          }}>
          <RefreshCw size={14} /> Refresh
        </button>

        <button
          onClick={() => setShowImport(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 10px', borderRadius: 8,
            border: `1px solid ${theme.colors.border}`, background: '#fff',
            fontSize: 13, cursor: 'pointer',
          }}
        >
          <Upload size={14} /> Import CSV
        </button>

        <button
          onClick={async () => {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              const r = await fetch('/api/export-deals', {
                headers: { Authorization: `Bearer ${session?.access_token}` },
              });
              if (!r.ok) return;
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `trovva_deals_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (e) { console.error('Export failed:', e); }
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 10px', borderRadius: 8,
            border: `1px solid ${theme.colors.border}`, background: '#fff',
            fontSize: 13, cursor: 'pointer',
          }}
        >
          <Download size={14} /> Export CSV
        </button>

        <div style={{ flex: 1 }} />

        <div style={{
          display: 'inline-flex', border: `1px solid ${theme.colors.border}`,
          borderRadius: 8, overflow: 'hidden',
        }}>
          <button
            onClick={() => setView('kanban')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', fontSize: 13, border: 'none', cursor: 'pointer',
              background: view === 'kanban' ? theme.colors.primary : '#fff',
              color: view === 'kanban' ? '#fff' : theme.colors.text,
              fontWeight: view === 'kanban' ? 600 : 500,
            }}
          >
            <LayoutGrid size={14} /> Kanban
          </button>
          <button
            onClick={() => setView('list')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', fontSize: 13, border: 'none', cursor: 'pointer',
              background: view === 'list' ? theme.colors.primary : '#fff',
              color: view === 'list' ? '#fff' : theme.colors.text,
              fontWeight: view === 'list' ? 600 : 500,
            }}
          >
            <ListIcon size={14} /> List
          </button>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight }}>
          Loading deals…
        </div>
      ) : error ? (
        <div style={{
          padding: 16, borderRadius: 8, background: '#FEF2F2', color: '#991B1B',
          border: '1px solid #FECACA',
        }}>
          Couldn't load deals: {error}
        </div>
      ) : view === 'kanban' ? (
        <KanbanBoard columns={columns} stages={activeStages} onOpen={id => setSelectedId(id)} />
      ) : filtered.length === 0 ? (
        <EmptyState onCreate={() => setShowNewDeal(true)} hasAny={deals.length > 0} />
      ) : (
        <DealList deals={filtered} onOpen={id => setSelectedId(id)} />
      )}

      {/* Drawer */}
      {selectedId && (
        <DealDetailDrawer
          dealId={selectedId}
          onClose={() => setSelectedId(null)}
          onChange={load}
        />
      )}

      {/* New Deal modal */}
      {showNewDeal && (
        <NewDealModal
          onClose={() => setShowNewDeal(false)}
          onCreate={createDeal}
        />
      )}

      {/* CSV Import modal */}
      {showImport && (
        <CsvDealImport
          onClose={() => setShowImport(false)}
          onDone={load}
        />
      )}
    </div>
  );
};

// ── Summary tile ──────────────────────────────────────────────
const SummaryTile = ({ icon, label, value, sub, tone }) => (
  <div style={{
    background: '#fff', border: `1px solid ${theme.colors.border}`,
    borderRadius: 10, padding: 14,
    borderLeft: tone === 'warning' ? '3px solid #EF4444' : `3px solid ${theme.colors.primary}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      {icon}
      <span style={{ fontSize: 12, color: theme.colors.textLight, fontWeight: 500 }}>{label}</span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── Kanban board ─────────────────────────────────────────────
const KanbanBoard = ({ columns, stages = RESIDENTIAL_STAGES, onOpen }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(${stages.length}, minmax(230px, 1fr))`,
    gap: 10,
  }}>
    {stages.map(stage => {
      const cards = columns[stage.key] || [];
      const total = cards.reduce((s, c) => s + (Number(c.agent_payout) || 0), 0);
      return (
        <div key={stage.key} style={{
          background: '#F8FAFC', border: `1px solid ${theme.colors.border}`,
          borderRadius: 10, padding: 10, minHeight: 240,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #E2E8F0',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: stage.color }} />
            <span style={{ fontWeight: 600, color: theme.colors.text, fontSize: 13 }}>{stage.label}</span>
            <span style={{ marginLeft: 'auto', color: theme.colors.textLight, fontSize: 12 }}>
              {cards.length}
            </span>
          </div>
          {total > 0 && (
            <div style={{ fontSize: 11, color: theme.colors.textLight, marginBottom: 8 }}>
              {fmtMoney(total)} forecast
            </div>
          )}
          <div style={{ display: 'grid', gap: 6 }}>
            {cards.map(d => <KanbanCard key={d.id} deal={d} onOpen={onOpen} />)}
            {cards.length === 0 && (
              <div style={{ fontSize: 12, color: theme.colors.textLight, padding: 6 }}>
                Nothing here
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

const KanbanCard = ({ deal, onOpen }) => {
  const risks = riskFor(deal);
  const dClose = daysUntil(deal.closing_date);
  return (
    <button
      onClick={() => onOpen(deal.id)}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        background: '#fff', border: `1px solid ${risks.length ? '#FECACA' : theme.colors.border}`,
        borderRadius: 8, padding: 10, cursor: 'pointer',
      }}
    >
      <div style={{
        fontWeight: 600, fontSize: 13, color: theme.colors.text,
        marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {addressOf(deal)}
      </div>
      <div style={{ fontSize: 11, color: theme.colors.textLight, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{clientOf(deal) || ({ seller: 'Seller rep', buyer: 'Buyer rep', lease: 'Lease', commercial_sale: 'Commercial sale', commercial_lease: 'Commercial lease' }[deal.deal_type] || 'Buyer rep')}</span>
        {isCommercialDeal(deal) && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
            background: '#EDE9FE', color: '#7C3AED', textTransform: 'uppercase', letterSpacing: 0.3,
          }}>CRE</span>
        )}
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11, color: theme.colors.textLight,
      }}>
        {deal.agent_payout != null && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <DollarSign size={11} /> {fmtMoney(deal.agent_payout)}
          </span>
        )}
        {dClose != null && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            color: dClose < 0 ? '#DC2626' : dClose <= 7 ? '#EA580C' : theme.colors.textLight,
          }}>
            <Clock size={11} /> {dClose < 0 ? `${-dClose}d late` : `${dClose}d`}
          </span>
        )}
      </div>
      {risks.length > 0 && (
        <div style={{
          marginTop: 6, padding: '4px 6px', borderRadius: 4,
          background: '#FEF2F2', color: '#991B1B', fontSize: 11,
        }}>
          {risks[0]}
        </div>
      )}
    </button>
  );
};

// ── List view ────────────────────────────────────────────────
const DealList = ({ deals, onOpen }) => (
  <div style={{
    background: '#fff', border: `1px solid ${theme.colors.border}`,
    borderRadius: 10, overflow: 'hidden',
  }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1fr 1fr 30px',
      gap: 8, padding: '10px 14px',
      borderBottom: `1px solid ${theme.colors.border}`,
      fontSize: 12, fontWeight: 600, color: theme.colors.textLight,
      background: '#F8FAFC',
    }}>
      <div>Address</div><div>Client</div><div>Stage</div>
      <div>Closing</div><div>Agent payout</div><div>Agent</div><div />
    </div>
    {deals.map(d => {
      const risks = riskFor(d);
      const dClose = daysUntil(d.closing_date);
      return (
        <button
          key={d.id}
          onClick={() => onOpen(d.id)}
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1fr 1fr 30px',
            gap: 8, padding: '10px 14px', alignItems: 'center',
            width: '100%', textAlign: 'left', border: 'none',
            borderBottom: `1px solid ${theme.colors.border}`,
            background: risks.length ? '#FFFBEB' : '#fff', cursor: 'pointer',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontWeight: 600, fontSize: 13, color: theme.colors.text,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {addressOf(d)}
            </div>
            {risks[0] && (
              <div style={{ fontSize: 11, color: '#B91C1C' }}>⚠ {risks[0]}</div>
            )}
          </div>
          <div style={{ fontSize: 13, color: theme.colors.text }}>
            {clientOf(d) || <span style={{ color: theme.colors.textLight }}>—</span>}
          </div>
          <div>
            <Badge label={STATUS_LABEL[d.status] || d.status} color={STAGES.find(s => s.key === STATUS_TO_STAGE[d.status])?.color || '#64748B'} />
          </div>
          <div style={{
            fontSize: 13,
            color: dClose != null && dClose < 0 ? '#DC2626'
              : dClose != null && dClose <= 7 ? '#EA580C'
              : theme.colors.text,
          }}>
            {d.closing_date ? formatDate(d.closing_date)
              : <span style={{ color: theme.colors.textLight }}>—</span>}
            {dClose != null && dClose >= 0 && dClose <= 14 && (
              <span style={{ fontSize: 11, color: theme.colors.textLight, marginLeft: 4 }}>
                ({dClose}d)
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: theme.colors.text }}>
            {fmtMoney(d.agent_payout)}
          </div>
          <div style={{ fontSize: 12, color: theme.colors.textLight }}>
            {d.agent_name || '—'}
          </div>
          <ChevronRight size={16} color={theme.colors.textLight} />
        </button>
      );
    })}
  </div>
);

// ── Empty state ──────────────────────────────────────────────
const EmptyState = ({ hasAny }) => (
  <div style={{
    padding: 48, textAlign: 'center', background: '#fff',
    border: `1px dashed ${theme.colors.border}`, borderRadius: 12,
  }}>
    <Home size={32} color={theme.colors.textLight} />
    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 10, color: theme.colors.text }}>
      {hasAny ? 'No deals match your filter' : 'No active deals yet'}
    </div>
    <div style={{ color: theme.colors.textLight, fontSize: 13, marginTop: 4, maxWidth: 380, margin: '4px auto 0' }}>
      {hasAny
        ? 'Try clearing the search or stage filter.'
        : 'Deals are created when a lead reaches "Offer Accepted" on the CRM board.'}
    </div>
  </div>
);

// ── New deal modal (lightweight) ─────────────────────────────
const NewDealModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState({
    deal_type: 'buyer', status: 'new_lead',
    address: '', client_first: '', client_last: '',
    offer_price: '', closing_date: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async e => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      // Parent API expects contact_id / property_id; for the MVP we only
      // pass the basics and let the user flesh it out from the drawer.
      await onCreate({
        deal_type: form.deal_type,
        status: form.status,
        offer_price: form.offer_price ? Number(form.offer_price) : null,
        closing_date: form.closing_date || null,
        notes: [form.address && `Address: ${form.address}`,
          (form.client_first || form.client_last) && `Client: ${form.client_first} ${form.client_last}`.trim(),
        ].filter(Boolean).join('\n') || null,
      });
    } catch (e) { setErr(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        style={{
          width: '100%', maxWidth: 460, background: '#fff', borderRadius: 12,
          padding: 20, display: 'grid', gap: 10,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700 }}>New deal</div>
        <Row label="Type">
          <select value={form.deal_type} onChange={e => setForm(f => ({ ...f, deal_type: e.target.value }))}
            style={fieldS}>
            <option value="buyer">Buyer rep</option>
            <option value="seller">Seller rep</option>
            <option value="lease">Lease</option>
            <option value="commercial_sale">Commercial sale</option>
            <option value="commercial_lease">Commercial lease</option>
          </select>
        </Row>
        <Row label="Stage">
          <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            style={fieldS}>
            {getStagesForDeal(form.deal_type).flatMap(stage =>
              stage.statuses.map(s => STATUS_LABEL[s] ? <option key={s} value={s}>{STATUS_LABEL[s]}</option> : null)
            ).filter(Boolean)}
          </select>
        </Row>
        <Row label="Property">
          <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
            placeholder="123 Maple St, Haddonfield NJ" style={fieldS} />
        </Row>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <input value={form.client_first} onChange={e => setForm(f => ({ ...f, client_first: e.target.value }))}
            placeholder="Client first name" style={fieldS} />
          <input value={form.client_last} onChange={e => setForm(f => ({ ...f, client_last: e.target.value }))}
            placeholder="Client last name" style={fieldS} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Row label="Offer $">
            <input type="number" value={form.offer_price}
              onChange={e => setForm(f => ({ ...f, offer_price: e.target.value }))}
              placeholder="750000" style={fieldS} />
          </Row>
          <Row label="Closing">
            <input type="date" value={form.closing_date}
              onChange={e => setForm(f => ({ ...f, closing_date: e.target.value }))}
              style={fieldS} />
          </Row>
        </div>
        {err && <div style={{ color: '#B91C1C', fontSize: 12 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: '#fff', cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="submit" disabled={submitting}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: theme.colors.primary, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            {submitting ? 'Creating…' : 'Create deal'}
          </button>
        </div>
      </form>
    </div>
  );
};

const Row = ({ label, children }) => (
  <label style={{ display: 'grid', gap: 4 }}>
    <span style={{ fontSize: 12, color: theme.colors.textLight, fontWeight: 500 }}>{label}</span>
    {children}
  </label>
);

const fieldS = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: `1px solid ${theme.colors.border}`, fontSize: 13,
};

export default TransactionsView;
